import { RootState } from '@react-three/fiber/dist/declarations/src/core/store';
import { Camera, Intersection, Object3D, Raycaster, Vector2 } from 'three';
import RaycastEvents from '@modules/three/common/raycastEvents';
import { getRaycastEnabled } from '@modules/three/common/raycastControl';
import { threeStore } from '@modules/three/store/threeStore.ts';
import { ThreeRootState } from '@modules/three/common/BaseThreeScene';
import { toolManager } from '../tools/ToolManager';

function hasRaycastHit(obj: unknown): obj is RaycastEvents {
  return typeof obj === 'object' && obj !== null && 'raycastHit' in obj;
}

function isRaycastDisabled(obj: Object3D): boolean {
  return !getRaycastEnabled(obj);
}

// Walk up from the hit object to find the first ancestor that handles raycastHit
// and is not disabled. If any ancestor on the path is explicitly disabled,
// the entire hit is considered disabled.
function findEnabledRaycastTarget(obj: Object3D | null): Object3D | null {
  let cur: Object3D | null = obj;
  let seenDisabled = false;
  while (cur) {
    if (isRaycastDisabled(cur)) {
      seenDisabled = true;
    }
    if (hasRaycastHit(cur)) {
      return seenDisabled ? null : cur;
    }
    cur = cur.parent;
  }
  return null;
}

export default class RaycasterClass {
  constructor() {}

  raycast(event: PointerEvent, three: RootState | ThreeRootState) {
      const gizmo = threeStore.getState().rootState.gizmo;
  
      setTimeout(() => {
        if (gizmo && gizmo.dragging) return;
  
        const pointer = new Vector2();
        const raycaster = new Raycaster();
        pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
        pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
        raycaster.setFromCamera(pointer, three.camera);
  
        const hits = raycaster.intersectObjects(three.scene.children, false);
        let hit: Intersection | null = null;
        for (const h of hits) {
          const target = findEnabledRaycastTarget(h.object);
          if (target) {
            // Redirect the hit to the enabled target object
            (h as any).object = target;
            hit = h;
            break;
          }
        }
  
        const active = toolManager.active;
        if (active) {
          switch (event.type) {
            case 'pointerdown': active.onPointerDown?.(event, hit, three); break;
            case 'pointermove': active.onPointerMove?.(event, hit, three); break;
            case 'pointerup':   active.onPointerUp?.(event, hit, three);   break;
          }
          return;
        }
        
  
        if (hit && event.type === 'pointerdown') {
          const obj = hit.object as Object3D;
          if (hasRaycastHit(obj)) obj.raycastHit(hit);
        }
      }, 1);
    }

  static getFirstObjectIntersection(
    event: PointerEvent,
    object: Object3D,
    camera: Camera = threeStore.getState().rootState.camera,
    recursive: boolean = false,
  ): Intersection | null {
    const raycaster = RaycasterClass.getRaycaster(event, camera);
    const intersects = raycaster.intersectObject(object, recursive);
    for (const h of intersects) {
      const target = findEnabledRaycastTarget(h.object);
      if (target) {
        (h as any).object = target;
        return h;
      }
    }
    return null;
  }

  static getFirstObjectsIntersection(
    event: PointerEvent,
    objects: Object3D[],
    camera: Camera = threeStore.getState().rootState.camera,
    recursive: boolean = false,
  ): { intersection: Intersection; index: number } | null {
    const raycaster = RaycasterClass.getRaycaster(event, camera);
    for (let i = 0; i < objects.length; i++) {
      const localIntersects = raycaster.intersectObject(objects[i], recursive);
      for (const h of localIntersects) {
        const target = findEnabledRaycastTarget(h.object);
        if (target) {
          (h as any).object = target;
          return { intersection: h, index: i };
        }
      }
    }
    return null;
  }

  static getRaycaster(event: PointerEvent, camera: Camera): Raycaster {
    const pointer = new Vector2();
    pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
    pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
    const raycaster = new Raycaster();
    raycaster.setFromCamera(pointer, camera);
    return raycaster;
  }
}
